import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Dropdown,
  Option,
  Textarea,
  Switch,
  Spinner,
  MessageBar,
  MessageBarBody,
  Checkbox,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  useCreateClientStateTaxObligation,
  useUpdateClientStateTaxObligation,
  useUpsertObligationInfoSource,
  STATE_TAX_OBLIGATION_OPTIONS,
  ApiError,
} from '../../services/stateTaxClientsApi';
import type { StateTaxObligationSummary, SuggestedObligation } from '../../types';
import { obligationSubmitsViaPatch } from './obligationSubmitMode';
import { seedInfoSources } from './obligationInfoSourceHelpers';
import { CalendarDateInput } from '../dates/CalendarDateInput';
import { cadenceName } from '../../utils/cadenceName';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
  },
  errorBar: {
    marginBottom: tokens.spacingVerticalS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    marginTop: tokens.spacingVerticalXS,
  },
  // S3 batch2: multi-select info-source list
  infoSourceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
  },
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateTaxClientId: string;
  /** Default state to prefill (e.g. "FL"). */
  defaultState?: string;
  /**
   * Pass an existing obligation to switch the dialog into edit mode.
   *   - If source === "explicit": Save sends a PATCH to update that Dataverse row.
   *   - If source === "cosmos": Save sends a PATCH; the backend routes the write to Cosmos.
   *   - If source === "derived": Save sends a POST that creates a new explicit row,
   *     which then takes precedence over the derived synthesis on the next read.
   */
  obligation?: StateTaxObligationSummary;
  /** Prefill from a suggestion (filing-history-derived). Always create-mode. */
  suggestion?: SuggestedObligation;
  /** Default information source to seed when neither obligation nor suggestion sets it. */
  defaultInformationSource?: string;
}

interface FormState {
  taxType: string;
  state: string;
  filingFrequency: string;
  // S3 batch2: multi-select — replaces single informationSource string
  informationSources: string[];
  hasDirectAccess: boolean;
  notes: string;
  effectiveStartDate: string; // YYYY-MM-DD or "" for unset
}

const EMPTY_FORM: FormState = {
  taxType: '',
  state: 'FL',
  filingFrequency: '',
  informationSources: [],
  hasDirectAccess: true,
  notes: '',
  effectiveStartDate: '',
};

// Trim a server-provided ISO timestamp down to YYYY-MM-DD for an HTML date input.
function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function formFromObligation(o: StateTaxObligationSummary): FormState {
  return {
    taxType: o.taxType,
    state: o.stateLabel ?? 'FL',
    filingFrequency: o.filingFrequencyLabel ?? '',
    informationSources: seedInfoSources(o),
    hasDirectAccess: o.hasDirectAccess ?? true,
    notes: o.notes ?? '',
    effectiveStartDate: isoToDateInput(o.effectiveStartDate ?? o.reportingSinceDate),
  };
}

export default function AddStateTaxObligationDialog({
  open,
  onOpenChange,
  stateTaxClientId,
  defaultState = 'FL',
  obligation,
  suggestion,
  defaultInformationSource,
}: Props) {
  const { t } = useTranslation();
  const styles = useStyles();
  const isEditPatch = obligationSubmitsViaPatch(obligation?.source);
  const isEditDerived = obligation?.source === 'derived';
  const isFromSuggestion = !obligation && !!suggestion;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const createMutation = useCreateClientStateTaxObligation();
  const updateMutation = useUpdateClientStateTaxObligation();
  // S3 batch2: info-source upsert mutation
  const infoSourceMutation = useUpsertObligationInfoSource(stateTaxClientId);

  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  useEffect(() => {
    if (open) {
      let next: FormState;
      if (obligation) {
        next = formFromObligation(obligation);
      } else if (suggestion) {
        next = {
          ...EMPTY_FORM,
          taxType: suggestion.taxType,
          state: suggestion.suggestedState || defaultState,
          filingFrequency: suggestion.suggestedFilingFrequency,
          informationSources: defaultInformationSource ? [defaultInformationSource] : [],
          effectiveStartDate: isoToDateInput(suggestion.reportingSinceDate),
        };
      } else {
        next = {
          ...EMPTY_FORM,
          state: defaultState,
          informationSources: defaultInformationSource ? [defaultInformationSource] : [],
        };
      }
      setForm(next);
      setErrors({});
      setSubmitError(null);
    }
  }, [open, obligation, suggestion, defaultState, defaultInformationSource]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }

  // S3 batch2: toggle one source in/out of the multi-select list
  function toggleInfoSource(source: string) {
    setForm(prev => {
      const has = prev.informationSources.includes(source);
      const next = has
        ? prev.informationSources.filter(s => s !== source)
        : [...prev.informationSources, source];
      return { ...prev, informationSources: next };
    });
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.taxType.trim()) next.taxType = t('stateTaxObligationDialog.validation.taxTypeRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSubmitError(null);

    // S3 batch2: informationSource is no longer sent to Dataverse create/update.
    // The info-source upsert runs separately via the new PUT endpoint.
    const payload = {
      taxType: form.taxType.trim(),
      state: form.state.trim() || undefined,
      filingFrequency: form.filingFrequency.trim() || undefined,
      // intentionally omit informationSource — no longer written to Dataverse
      hasDirectAccess: form.hasDirectAccess,
      notes: form.notes.trim() || undefined,
      active: true,
      effectiveStartDate: form.effectiveStartDate || undefined,
    };

    try {
      let savedObligationId: string | undefined;

      if (isEditPatch && obligation) {
        const editPayload = {
          ...payload,
          changedByName: activeAccount?.name ?? activeAccount?.username,
          changedById: activeAccount?.localAccountId,
          expectedEtag: obligation.etag,
        };
        await updateMutation.mutateAsync({
          stateTaxClientId,
          obligationId: obligation.id,
          payload: editPayload,
        });
        savedObligationId = obligation.id;
      } else {
        const created = await createMutation.mutateAsync({ stateTaxClientId, payload });
        // created may be null/undefined when the create returned no body — best effort
        savedObligationId = (created as StateTaxObligationSummary | null | undefined)?.id;
      }

      // S3 batch2: persist info sources via the new app-owned endpoint.
      // Runs after the obligation save so we have the obligationId.
      if (savedObligationId) {
        await infoSourceMutation.mutateAsync({
          obligationId: savedObligationId,
          sources: form.informationSources,
          updatedBy: activeAccount?.name ?? activeAccount?.username,
        });
      }

      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError(
          err.message
          || t('stateTaxObligationDialog.conflict'),
        );
      } else {
        setSubmitError(err instanceof Error ? err.message : t('stateTaxObligationDialog.unexpectedError'));
      }
    }
  }

  const isPending =
    createMutation.isPending || updateMutation.isPending || infoSourceMutation.isPending;
  const mode = isEditPatch ? 'editPatch' : isEditDerived ? 'editDerived' : isFromSuggestion ? 'fromSuggestion' : 'add';
  const title = t(`stateTaxObligationDialog.title.${mode}`);
  const submitLabel = t(`stateTaxObligationDialog.submit.${mode}`);

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!isPending) onOpenChange(data.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {submitError && (
              <MessageBar intent="error" className={styles.errorBar}>
                <MessageBarBody>{submitError}</MessageBarBody>
              </MessageBar>
            )}
            {isEditDerived && (
              <MessageBar intent="info" className={styles.errorBar}>
                <MessageBarBody>
                  {t('stateTaxObligationDialog.legacyRow')}
                </MessageBarBody>
              </MessageBar>
            )}
            {isFromSuggestion && suggestion && (
              <MessageBar intent="info" className={styles.errorBar}>
                <MessageBarBody>
                  {t('stateTaxObligationDialog.suggested', { count: suggestion.fileCount })}
                  {suggestion.latestFilingDate
                    ? t('stateTaxObligationDialog.suggestedLatest', { date: suggestion.latestFilingDate.slice(0, 10) })
                    : ''}.
                  {suggestion.sampleFilenames.length > 0 && (
                    <> {t('stateTaxObligationDialog.examples')} <em>{suggestion.sampleFilenames.slice(0, 2).join(', ')}</em>.</>
                  )}
                </MessageBarBody>
              </MessageBar>
            )}
            <div className={styles.form}>
              <Field label={t('stateTaxObligationDialog.taxType')} required validationMessage={errors.taxType}>
                <Dropdown
                  placeholder={t('stateTaxObligationDialog.selectTaxType')}
                  value={form.taxType}
                  selectedOptions={form.taxType ? [form.taxType] : []}
                  onOptionSelect={(_e, data) => setField('taxType', data.optionValue ?? '')}
                  disabled={isEditPatch}
                >
                  {STATE_TAX_OBLIGATION_OPTIONS.taxType.map(taxType => (
                    <Option key={taxType} value={taxType}>
                      {taxType}
                    </Option>
                  ))}
                </Dropdown>
                {isEditPatch && (
                  <span className={styles.hint}>{t('stateTaxObligationDialog.taxTypeLocked')}</span>
                )}
              </Field>

              <div className={styles.row}>
                <Field label={t('stateTaxObligationDialog.state')}>
                  <Dropdown
                    value={form.state}
                    selectedOptions={form.state ? [form.state] : []}
                    onOptionSelect={(_e, data) => setField('state', data.optionValue ?? '')}
                  >
                    {STATE_TAX_OBLIGATION_OPTIONS.state.map(s => (
                      <Option key={s} value={s}>
                        {s}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
                <Field label={t('stateTaxObligationDialog.filingFrequency')}>
                  <Dropdown
                    placeholder={t('stateTaxObligationDialog.selectFrequency')}
                    value={form.filingFrequency ? cadenceName(t, form.filingFrequency) : ''}
                    selectedOptions={form.filingFrequency ? [form.filingFrequency] : []}
                    onOptionSelect={(_e, data) => setField('filingFrequency', data.optionValue ?? '')}
                  >
                    {STATE_TAX_OBLIGATION_OPTIONS.filingFrequency.map(f => (
                      <Option key={f} value={f}>
                        {cadenceName(t, f)}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>

              {/* S3 batch2: multi-select info sources via checkboxes (avoids Fluent popup
                  jsdom hang). Options from STATE_TAX_OBLIGATION_OPTIONS.informationSource. */}
              <Field label={t('stateTaxObligationDialog.informationSource')} hint={t('stateTaxObligationDialog.informationSourceHint')}>
                <div className={styles.infoSourceList}>
                  {STATE_TAX_OBLIGATION_OPTIONS.informationSource.map(src => (
                    <Checkbox
                      key={src}
                      label={src}
                      checked={form.informationSources.includes(src)}
                      onChange={() => toggleInfoSource(src)}
                    />
                  ))}
                </div>
              </Field>

              <div className={styles.row}>
                <Field label={t('stateTaxObligationDialog.directAccess')}>
                  <Switch
                    checked={form.hasDirectAccess}
                    onChange={(_e, data) => setField('hasDirectAccess', data.checked)}
                    label={form.hasDirectAccess ? 'Yes' : 'No'}
                  />
                </Field>
                <Field
                  label={t('stateTaxObligationDialog.reportingSince')}
                  hint={t('stateTaxObligationDialog.reportingSinceHint')}
                >
                  <CalendarDateInput value={form.effectiveStartDate} onChange={(v) => setField('effectiveStartDate', v)} />
                </Field>
              </div>

              <Field label={t('stateTaxObligationDialog.notes')}>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(_e, data) => setField('notes', data.value)}
                  placeholder={t('stateTaxObligationDialog.notesPlaceholder')}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('stateTaxObligationDialog.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabled={isPending}
              icon={isPending ? <Spinner size="tiny" /> : undefined}
              onClick={() => {
                void handleSubmit();
              }}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
